import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
  ServiceStatus, MaintenanceWindow, MaintenanceForm, ScheduleForm,
  SERVICES, STATUS_OPTIONS, EMPTY_FORM, EMPTY_SCHEDULE,
} from './maintenanceTypes';

export function useMaintenanceData() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [form, setForm] = useState<MaintenanceForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editStatusService, setEditStatusService] = useState<string | null>(null);
  const [editStatusValue, setEditStatusValue] = useState<ServiceStatus['status']>('operational');
  const [editStatusMessage, setEditStatusMessage] = useState('');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>(EMPTY_SCHEDULE);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotif = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, windowsRes] = await Promise.all([
        supabase.rpc('admin_get_system_status'),
        supabase.rpc('admin_get_maintenance_windows'),
      ]);
      if (statusRes.error) throw statusRes.error;
      if (windowsRes.error) throw windowsRes.error;
      setServices(statusRes.data || []);
      setWindows(windowsRes.data || []);
    } catch (err: any) {
      showNotif(`Failed to load: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotif]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isInMaintenance = services.some(s => s.status === 'maintenance');
  const activeWindows = windows.filter(w => w.status === 'in_progress' || w.status === 'scheduled');
  const pastWindows = windows.filter(w => w.status === 'completed' || w.status === 'cancelled');

  const handleEnableMaintenance = async () => {
    if (!form.title.trim()) { showNotif('Title is required', 'error'); return; }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_enable_maintenance_mode', {
        p_title: form.title.trim(),
        p_description: form.description.trim(),
        p_duration_hours: parseFloat(form.duration_hours) || 2,
        p_services: form.services,
      });
      if (error) throw error;
      showNotif('Maintenance mode enabled', 'success');
      setShowMaintenanceForm(false);
      setForm(EMPTY_FORM);
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableMaintenance = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_disable_maintenance_mode');
      if (error) throw error;
      showNotif(`Maintenance disabled — ${data?.windows_completed || 0} window(s) completed`, 'success');
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    }
  };

  const handleSetServiceStatus = async () => {
    if (!editStatusService) return;
    const statusOpt = STATUS_OPTIONS.find(s => s.value === editStatusValue);
    const finalMessage = editStatusMessage.trim() || statusOpt?.defaultMessage || null;
    try {
      const { error } = await supabase.rpc('admin_set_service_status', {
        p_service_name: editStatusService,
        p_status: editStatusValue,
        p_message: finalMessage,
      });
      if (error) throw error;
      showNotif(`${editStatusService} → ${editStatusValue}`, 'success');
      setEditStatusService(null);
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    }
  };

  const handleScheduleMaintenance = async () => {
    if (!scheduleForm.title.trim() || !scheduleForm.start_date || !scheduleForm.start_time) {
      showNotif('Title, date, and time are required', 'error'); return;
    }
    setSaving(true);
    try {
      const startTime = new Date(`${scheduleForm.start_date}T${scheduleForm.start_time}`);
      const endTime = new Date(startTime.getTime() + parseFloat(scheduleForm.duration_hours) * 3600000);
      const { error } = await supabase.rpc('admin_create_maintenance_window', {
        p_title: scheduleForm.title.trim(),
        p_description: scheduleForm.description.trim(),
        p_start_time: startTime.toISOString(),
        p_end_time: endTime.toISOString(),
        p_affected_services: scheduleForm.services,
        p_status: 'scheduled',
        p_is_published: scheduleForm.is_published,
      });
      if (error) throw error;
      showNotif('Maintenance window scheduled', 'success');
      setShowScheduleForm(false);
      setScheduleForm({ title: '', description: '', start_date: '', start_time: '', duration_hours: '2', services: [...SERVICES], is_published: true });
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWindowStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase.rpc('admin_update_maintenance_window', {
        p_id: id, p_status: status,
      });
      if (error) throw error;
      showNotif(`Window ${status}`, 'success');
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    }
  };

  const handleDeleteWindow = async (id: string) => {
    try {
      const { error } = await supabase.rpc('admin_delete_maintenance_window', { p_id: id });
      if (error) throw error;
      setDeleteConfirm(null);
      showNotif('Window deleted', 'success');
      await fetchData();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    }
  };

  const toggleFormService = (svc: string, formType: 'maintenance' | 'schedule') => {
    if (formType === 'maintenance') {
      setForm(prev => ({
        ...prev,
        services: prev.services.includes(svc) ? prev.services.filter(s => s !== svc) : [...prev.services, svc],
      }));
    } else {
      setScheduleForm(prev => ({
        ...prev,
        services: prev.services.includes(svc) ? prev.services.filter(s => s !== svc) : [...prev.services, svc],
      }));
    }
  };

  const getStatusInfo = (status: string) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  return {
    services, loading, isInMaintenance, activeWindows, pastWindows,
    showMaintenanceForm, setShowMaintenanceForm, form, setForm,
    saving, editStatusService, setEditStatusService, editStatusValue, setEditStatusValue,
    editStatusMessage, setEditStatusMessage, showScheduleForm, setShowScheduleForm,
    scheduleForm, setScheduleForm, deleteConfirm, setDeleteConfirm,
    notification, setNotification, fetchData,
    handleEnableMaintenance, handleDisableMaintenance, handleSetServiceStatus,
    handleScheduleMaintenance, handleUpdateWindowStatus, handleDeleteWindow,
    toggleFormService, getStatusInfo,
  };
}

export type MaintenanceModel = ReturnType<typeof useMaintenanceData>;
